import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, omit } from 'lodash';
import Form, { ActionEvent, FormAction, FormRefType } from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import type { Asset, AssetInput } from '#/graphql/types';
import useAssetUpdate from '../../hooks/useUpdateAssetMutation';
import useAssetCreate from '../../hooks/useCreateAssetMutation';
import useAssetDelete from '../../hooks/useDeleteAssetMutation';
import useModelListLazyQuery from '../../hooks/useModelListLazyQuery';
import { createFields, updateFields } from './fields';
import allFields from '../../utils/fields';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import ScanSelector from '#/common/components/ScanSelector';
import { OMIT_FIELDS } from '#/config/consts';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { states } from '../../utils/mappings';
import dxForm, { FieldDataChangedEvent } from 'devextreme/ui/form';
import { InitializedEventInfo } from 'devextreme/events';
import {
  getItemOptions,
  setEditorOptions,
  setItemOptions,
} from '#/common/components/Form/formItemOption';
import useOcrProcessing, { OcrFieldResult } from '#/common/hooks/useOcrProcessing';
import useUploadScannedFile from '#/common/hooks/useUploadScannedFile';
import { extractFieldNames } from '#/common/utils/extractFieldNames';
import { getHistoryFieldNames } from '#/common/utils/fields/historySection';

// OCR field constants for asset nameplates
const OCR_SERIAL_NUMBER = 'serial number';
const OCR_MODEL_NUMBER = 'model number';

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: false,
  [UserRole.trane_admin]: false,
  [UserRole.technician]: true,
};

export interface AssetFormProps {
  asset: Asset;
  title: string;
}

const AssetForm: React.FC<AssetFormProps> = ({ asset, title }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const { user } = useSession();
  const role = user?.role as UserRole;
  const formRef = useRef<FormRefType | null>(null);
  const [scannedFile, setScannedFile] = useState<File | null>(null);
  const uploadScannedFile = useUploadScannedFile();
  const { getOcrSuccessMessage } = useOcrProcessing();
  const id = get(asset, 'id', undefined);
  const fields = id ? updateFields : createFields;
  const isReadonly = formReadonlyRole[role] || asset?.state === states.retired.key;
  // Form is read-only only when the saved asset state is retired (after submit)
  const historyFieldNames = getHistoryFieldNames();

  const allFieldNames = useMemo(() => {
    return extractFieldNames(fields, historyFieldNames);
  }, [fields, historyFieldNames]);

  // Set all fields to read-only (used after successful save when state is retired)
  const setFormFieldsReadOnly = useCallback(
    (form: dxForm) => {
      allFieldNames.forEach((fieldName) => {
        setEditorOptions(form, fieldName, { readOnly: true });
      });
    },
    [allFieldNames],
  );

  // State for ScanSelector
  const [showAssetScanSelector, setShowAssetScanSelector] = useState(!id); // Show for new assets
  const [tenantId, setTenantId] = useState<string | undefined>(get(asset, 'tenantId'));

  // Defined in the modules hooks dir
  const { updateAsset } = useAssetUpdate();
  const { createAsset } = useAssetCreate();
  const { deleteAsset } = useAssetDelete();
  const fetchModelList = useModelListLazyQuery();

  // Handle asset scan selection
  const fieldsToScan = [OCR_SERIAL_NUMBER, OCR_MODEL_NUMBER];
  const handleAssetScanSelect = useCallback(
    async (file: File | null, result: OcrFieldResult, error?: string) => {
      if (!formRef.current) return;

      if (error) {
        console.error('Error processing image:', error);
        void alert('error', `Error processing image: ${error}`);
        return;
      }

      if (file) {
        setScannedFile(file);
      }

      const populatedFields: string[] = [];

      if (result[OCR_SERIAL_NUMBER]) {
        formRef.current?.updateData('serialNumber', result[OCR_SERIAL_NUMBER]);
        populatedFields.push(allFields.serialNumber.label);
      }
      if (result[OCR_MODEL_NUMBER]) {
        formRef.current?.updateData('description', result[OCR_MODEL_NUMBER]);
        populatedFields.push(allFields.description.label);

        // Auto-populate assetTypeId, makeId and modelId if the first 8 characters match the result[OCR_MODEL_NUMBER]
        const { data: modelListData } = await fetchModelList({
          variables: {
            params: {
              filter: JSON.stringify([
                ['tenantId', '=', tenantId ?? ''],
                'AND',
                ['inUseStatus', '=', 'inUse'],
                'AND',
                ['name', 'startswith', result[OCR_MODEL_NUMBER].substring(0, 8)],
              ]),
              requireTotalCount: false,
              skip: 0,
              take: 1,
            },
          },
        });

        const models = modelListData?.getModelList?.data ?? [];
        if (models.length > 0 && models[0]) {
          const matchedModel = models[0];
          formRef.current?.updateData('assetTypeId', matchedModel.assetTypeId);
          formRef.current?.updateData('makeId', matchedModel.manufacturerId);
          formRef.current?.updateData('modelId', matchedModel.id);
          populatedFields.push(allFields.assetTypeId.label);
          populatedFields.push(allFields.makeId.label);
          populatedFields.push(allFields.modelId.label);
        }
      }

      // Repaint the form to show the updated values
      formRef.current?.repaint();
      setShowAssetScanSelector(false);

      // Show success alert with populated fields
      const successMessage = getOcrSuccessMessage(populatedFields);
      if (successMessage) {
        void alert('success', successMessage);
      } else {
        void alert('warning', "Couldn't read the nameplate. You can edit fields manually.");
      }
    },
    [tenantId, fetchModelList],
  );

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteAsset(id);
    },
    [id, deleteAsset],
  );

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: Asset) => {
      if (!id) {
        return createAsset(omit(data, OMIT_FIELDS) as AssetInput);
      }
      return updateAsset(id, omit(data, OMIT_FIELDS) as AssetInput);
    },
    [id, updateAsset, createAsset],
  );

  const handleSubmissionResult = async (result: Asset | undefined, e: ActionEvent<Asset>) => {
    if (!result) {
      // Handle error if result is undefined
      void alert('error', 'Failed to save asset');
      return true;
    }

    if (!id && result.id) {
      // Upload scanned file if available
      if (scannedFile) {
        if (!tenantId) {
          throw new Error('Tenant ID is required for scanned file upload');
        }
        await uploadScannedFile('assets', result.id, scannedFile, tenantId);
        setScannedFile(null);
      }
      void alert('success', 'Changes saved!');
      navigate(`/assets/${result.id}/details`, { replace: true });
      return true;
    }
    return false;
  };

  const actions = useMemo(() => {
    const result: FormAction<Asset>[] = [];

    if (!isReadonly && deleteItem && id) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const sure = await confirm(
            'error',
            `Are you sure you want to delete asset: ${e.record.displayNumber}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteItem((e.record as Asset).id);
          e.setIsLoading(false);

          navigate('/assets');
        },
      });
    }

    if (!isReadonly && id) {
      result.push({
        label: 'Generate Maintenance Plan',
        type: 'normal',
        disabled: isReadonly,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          navigate(`/assets/${id}/maintenance-plan`);
        },
      });
    }

    if (!isReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        disabled: isReadonly,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          if (!validateForm(e)) return;
          if (await confirmRetirement(e)) return;
          try {
            e.setIsLoading(true);
            const result = (await submit(e.record)) as Asset | undefined;

            if (await handleSubmissionResult(result, e)) {
              return;
            }

            e.clearDirty();
            void alert('success', 'Changes saved!');

            // If state is retired, make all fields read-only after successful save
            if (e.record?.state === states.retired.key && e.form) {
              setFormFieldsReadOnly(e.form);
            }

            formRef.current?.repaint();
          } catch (err: any) {
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          } finally {
            e.setIsLoading(false);
          }
          return;
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, asset, deleteItem, scannedFile]);

  const validateForm = (e: ActionEvent<Asset>) => {
    const validation = e.form?.validate();
    if (
      !validation?.isValid &&
      e.record &&
      getCleanFormErrors(validation, allFields, e.record).length
    ) {
      void alert('error', `${title} is not valid, check field entries.`);
      return false;
    }
    return true;
  };

  const confirmRetirement = async (e: ActionEvent<Asset>) => {
    if (e?.record?.state === states.retired.key) {
      const sure = await confirm(
        'warning',
        'This action will set the asset to "retired", lock the form for editing, and archive its maintenance plan. Continue?',
      );
      return !sure;
    }
    return false;
  };

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;
    if (!form) return;

    const formData = form.option('formData') as Asset;

    // Dynamically patch image field to show model attachment
    const imageField = getItemOptions(form, 'image');
    if (imageField?.editorOptions) {
      setItemOptions(form, 'image', 'editorOptions', {
        ...imageField.editorOptions,
        previewOnly: true,
        relatedTable: 'models',
        relatedItemId: formData?.modelId ?? '',
      });
    }

    const makeOptions = { disabled: !formData.assetTypeId };
    const modelOptions = { disabled: !formData.makeId };
    setEditorOptions(form, 'makeId', makeOptions);
    setEditorOptions(form, 'modelId', modelOptions);
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField: field } = e;
    const formData = form.option('formData');
    if (field === 'assetTypeId') {
      const makeOptions = { disabled: !formData.assetTypeId };
      setEditorOptions(form, 'makeId', makeOptions);
      form.updateData('makeId', null);
    }
    if (field === 'makeId' || field === 'assetTypeId') {
      const modelOptions = { disabled: !formData.makeId };
      setEditorOptions(form, 'modelId', modelOptions);
      form.updateData('modelId', null);
    }
    if (field === 'tenantId') {
      setTenantId(formData.tenantId);
    }
  }, []);

  const onCloseAssetScanSelector = useCallback(() => {
    setShowAssetScanSelector(false);
  }, []);

  return (
    <>
      {/* Show ScanSelector for new assets after tenant is selected */}
      {showAssetScanSelector && tenantId && (
        <ScanSelector
          onOcrResult={handleAssetScanSelect}
          onClose={onCloseAssetScanSelector}
          modalChoiceText="Scan Asset's Nameplate"
          fieldsToScan={fieldsToScan}
        />
      )}
      <Form
        ref={formRef}
        readonly={isReadonly}
        record={asset as Asset}
        fields={fields}
        actions={actions}
        onFieldDataChanged={onFieldDataChanged}
        onContentReady={onContentReady}
        supportTenant
      />

      {id && (
        <AttachmentsUpload
          disableUpload={isReadonly}
          relatedTable="assets"
          relatedItem={id}
          tenantId={tenantId}
        />
      )}
    </>
  );
};

export default React.memo(AssetForm);
